import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useSavedMeals, SavedMealItem } from "@/hooks/useSavedMeals";
import { FoodSearchModal } from "./FoodSearchModal";
import { NutritionIcons } from "./NutritionIcons";

interface CreateMealModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editMeal?: {
    id: string;
    name: string;
    description?: string;
    meal_type?: 'breakfast' | 'lunch' | 'dinner' | 'snack';
    items: any[];
  } | null;
}

export const CreateMealModal = ({ open, onOpenChange, editMeal }: CreateMealModalProps) => {
  const { createSavedMeal, updateSavedMeal } = useSavedMeals();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [mealType, setMealType] = useState<'breakfast' | 'lunch' | 'dinner' | 'snack'>('breakfast');
  const [items, setItems] = useState<Omit<SavedMealItem, "id">[]>([]);
  const [showFoodSearch, setShowFoodSearch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Load edit data when editMeal changes
  useEffect(() => {
    if (editMeal && open) {
      setName(editMeal.name);
      setDescription(editMeal.description || "");
      setMealType(editMeal.meal_type || 'breakfast');
      setItems(editMeal.items.map(item => ({
        food_id: item.food_id,
        user_food_id: item.user_food_id,
        food_name: item.food_name,
        quantity: item.quantity,
        unit: item.unit,
        calories: item.calories,
        protein: item.protein,
        carbs: item.carbs,
        fat: item.fat,
        fiber: item.fiber,
      })));
    } else if (!open) {
      // Reset when closed
      setName("");
      setDescription("");
      setMealType('breakfast');
      setItems([]);
    }
  }, [editMeal, open]);


  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [editQuantity, setEditQuantity] = useState(1);
  const [editUnit, setEditUnit] = useState("serving");

  const handleEditItem = (index: number) => {
    const item = items[index];
    setEditingItemIndex(index);
    setEditQuantity(item.quantity);
    setEditUnit(item.unit);
  };

  const handleUpdateItem = () => {
    if (editingItemIndex === null) return;
    
    const item = items[editingItemIndex];
    const quantityRatio = editQuantity / item.quantity;
    
    const updatedItem = {
      ...item,
      quantity: editQuantity,
      unit: editUnit,
      calories: item.calories * quantityRatio,
      protein: item.protein * quantityRatio,
      carbs: item.carbs * quantityRatio,
      fat: item.fat * quantityRatio,
      fiber: (item.fiber || 0) * quantityRatio,
    };
    
    const updatedItems = [...items];
    updatedItems[editingItemIndex] = updatedItem;
    setItems(updatedItems);
    setEditingItemIndex(null);
  };

  // Helper to check if a string is a valid UUID
  const isValidUUID = (str: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      console.log('CreateMealModal: Name is empty');
      return;
    }

    if (items.length === 0) {
      console.log('CreateMealModal: No items');
      return;
    }

    try {
      console.log('CreateMealModal: Starting save with items:', items);
      console.log('CreateMealModal: Meal type being saved:', mealType);
      setSaving(true);
      const result = editMeal 
        ? await updateSavedMeal(editMeal.id, name, description, mealType, items)
        : await createSavedMeal(name, description, mealType, items);
      
      console.log('CreateMealModal: Save result:', result);
      setSaving(false);

      if (result) {
        console.log('CreateMealModal: Closing modal');
        setName("");
        setDescription("");
        setMealType('breakfast');
        setItems([]);
        onOpenChange(false);
      } else {
        console.log('CreateMealModal: Result was null/falsy, modal staying open');
      }
    } catch (error) {
      console.error('CreateMealModal: Error in handleSave:', error);
      setSaving(false);
    }
  };

  const totals = items.reduce(
    (acc, item) => ({
      calories: acc.calories + item.calories,
      protein: acc.protein + item.protein,
      carbs: acc.carbs + item.carbs,
      fat: acc.fat + item.fat,
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMeal ? 'Edit Meal' : 'Create Custom Meal'}</DialogTitle>
            <DialogDescription>
              {editMeal ? 'Update your meal template' : 'Create a meal template with multiple foods for quick logging'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="meal-name">Meal Name *</Label>
              <Input
                id="meal-name"
                placeholder="e.g., Protein Breakfast"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="meal-description">Description (optional)</Label>
              <Textarea
                id="meal-description"
                placeholder="Add notes about this meal..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="meal-type">Meal Type</Label>
              <select
                id="meal-type"
                value={mealType}
                onChange={(e) => setMealType(e.target.value as typeof mealType)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="breakfast">Breakfast</option>
                <option value="lunch">Lunch</option>
                <option value="dinner">Dinner</option>
                <option value="snack">Snack</option>
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Foods in this meal</Label>
                <Button size="sm" onClick={() => setShowFoodSearch(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Food
                </Button>
              </div>

              {items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border rounded-lg">
                  No foods added yet. Click "Add Food" to get started.
                </div>
              ) : (
                <div className="space-y-2">
                  {items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      {editingItemIndex === index ? (
                        <div className="flex-1 space-y-3">
                          <p className="font-medium">{item.food_name}</p>
                          <div className="flex gap-2">
                            <div className="flex-1">
                              <Label htmlFor={`edit-quantity-${index}`}>Quantity</Label>
                              <Input
                                id={`edit-quantity-${index}`}
                                type="number"
                                min="0.1"
                                step="0.1"
                                value={editQuantity}
                                onChange={(e) => setEditQuantity(parseFloat(e.target.value) || 1)}
                                onFocus={(e) => e.target.select()}
                              />
                            </div>
                            <div className="flex-1">
                              <Label htmlFor={`edit-unit-${index}`}>Unit</Label>
                              <select
                                id={`edit-unit-${index}`}
                                value={editUnit}
                                onChange={(e) => setEditUnit(e.target.value)}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                <option value="g">Grams</option>
                                <option value="serving">Serving</option>
                                <option value={item.unit}>{item.unit}</option>
                              </select>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingItemIndex(null)}
                              className="flex-1"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleUpdateItem}
                              className="flex-1"
                            >
                              Update
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex-1">
                            <p className="font-medium">{item.food_name}</p>
                            <p className="text-sm text-muted-foreground mb-1">
                              {item.quantity} {item.unit}
                            </p>
                            <NutritionIcons
                              calories={item.calories}
                              protein={item.protein}
                              carbs={item.carbs}
                              fat={item.fat}
                              iconSize={12}
                              className="text-muted-foreground"
                            />
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleEditItem(index)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleRemoveItem(index)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-semibold mb-2">Total Nutrition</p>
                <NutritionIcons
                  calories={totals.calories}
                  protein={totals.protein}
                  carbs={totals.carbs}
                  fat={totals.fat}
                  className="flex-wrap"
                />
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!name.trim() || items.length === 0 || saving}
              >
                {saving ? "Saving..." : editMeal ? "Update Meal" : "Save Meal"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <FoodSearchModal
        isOpen={showFoodSearch}
        onClose={() => {
          setShowFoodSearch(false);
          setEditingIndex(null);
        }}
        onSelectFood={(food, quantity, unit) => {
          // Determine which ID field to use based on food source
          let foodIdField: { food_id?: string; user_food_id?: string } = {};
          
          if (isValidUUID(food.id)) {
            // Check if it's a user-created food
            if ('user_id' in food) {
              foodIdField = { user_food_id: food.id };
            } 
            // Check if it's from basic_foods table (don't set any ID - just use name)
            else if ('source' in food && (food.source === 'basic' || (food as any).source_type === 'basic')) {
              // Don't set any ID for basic_foods - they have their own table
              foodIdField = {};
            }
            // Otherwise it's from the foods table
            else {
              foodIdField = { food_id: food.id };
            }
          }

          // Calculate nutritional values based on quantity and unit
          const multiplier = unit === 'serving' ? quantity : (quantity / food.serving_size);
          
          const newItem: Omit<SavedMealItem, "id"> = {
            ...foodIdField,
            food_name: food.name,
            quantity,
            unit,
            calories: food.calories_per_serving * multiplier,
            protein: food.protein_per_serving * multiplier,
            carbs: food.carbs_per_serving * multiplier,
            fat: food.fat_per_serving * multiplier,
            fiber: (food.fiber_per_serving || 0) * multiplier,
          };

          console.log('New item created:', newItem);

          if (editingIndex !== null) {
            // Update existing item
            const updatedItems = [...items];
            updatedItems[editingIndex] = newItem;
            setItems(updatedItems);
            setEditingIndex(null);
            console.log('Item updated at index:', editingIndex);
          } else {
            // Add new item
            setItems([...items, newItem]);
            console.log('New item added to list');
          }
        }}
      />
    </>
  );
};